/**
 * The event page's standard look: the service page's layout (the prototype's
 * Option 1, drawn from the service page's styles) with the event's one date
 * and time in the facts, in the visitor's zone, and its spots left. The photos
 * and booking column are their own pieces, passed in. Draws the behavior
 * half's state; holds no rule. Each line drops out when unset.
 */
import type { ReactNode } from 'react';
import { LOCATION_KIND_LABEL } from '@telify/shared';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { formatDayLabel, formatLocation, formatSlotTime, priceText } from '../../format';
import shared from '../../styles/shared.module.css';
import { MoveLink } from '../../ui/MoveLink';
import { HomeCrumbs, Paragraphs } from '../service-page/ServicePageView';
import css from '../service-page/ServicePage.module.css';
import type { EventPageRuntime } from './useEventPage';
import { contract } from './contract';

const part = partsOf(contract);

export interface EventPageSlots {
  /** The photo gallery piece. */
  readonly photos?: ReactNode;
  /** The booking column piece. */
  readonly booking?: ReactNode;
  /** Message the studio, under the details when her site turns it on (web-studio-sites D15). */
  readonly message?: ReactNode;
}

export function EventPageView({ state, options, photos, booking, message }: EventPageRuntime & EventPageSlots) {
  const e = state.event;
  if (state.status === 'error') {
    return (
      <div className={`${shared.wrap} ${css.page}`} {...pieceRoot(contract)} {...part('page')}>
        <p className={css.error} {...part('error')}>
          This page could not load. Please try again in a moment.
        </p>
      </div>
    );
  }
  if (!e) return <div className={css.page} aria-busy="true" {...pieceRoot(contract)} {...part('page')} />;

  const where = formatLocation(e.location);
  const when = `${formatDayLabel(e.startTime, state.timezone)} · ${formatSlotTime(e.startTime, state.timezone)}`;
  const spots = e.spotsLeft <= 0 ? 'Full' : e.spotsLeft === 1 ? '1 spot left' : `${e.spotsLeft} spots left`;
  return (
    <div className={css.page} {...pieceRoot(contract)} {...part('page')}>
      <div className={shared.wrap}>
        <nav className={css.crumbs} aria-label="Breadcrumb" {...part('crumbs')}>
          <HomeCrumbs
            state={state}
            between={
              <>
                <MoveLink to="/events" {...part('crumb-events')}>
                  Events
                </MoveLink>
                <span className={css.sep} aria-hidden="true">
                  /
                </span>
              </>
            }
          />
          <span className={css.here} aria-current="page" {...part('crumb-here')}>
            {e.title}
          </span>
        </nav>

        <div className={css.grid}>
          <article>
            <div className={css.photos} {...part('photos')}>
              {photos}
            </div>
            <div className={css.title}>
              <h1 className={css.h1} {...part('name')}>
                {e.title}
              </h1>
              {e.summary && (
                <p className={css.lead} {...part('summary')}>
                  {e.summary}
                </p>
              )}
            </div>
            <div className={css.facts} {...part('facts')}>
              <div className={css.fact} {...part('when')}>
                <div className={css.factK}>When</div>
                <div className={css.factV}>{when}</div>
              </div>
              <div className={css.fact} {...part('format')}>
                <div className={css.factK}>Format</div>
                <div className={css.factV}>{LOCATION_KIND_LABEL[e.modality]}</div>
              </div>
              <div className={css.fact} {...part('price')}>
                <div className={css.factK}>Price</div>
                <div className={`${css.factV} ${css.accent}`}>{priceText(e.price)}</div>
              </div>
              <div className={css.fact} {...part('spots')}>
                <div className={css.factK}>Spots</div>
                <div className={`${css.factV} ${e.spotsLeft <= 3 ? css.low : ''}`}>{spots}</div>
              </div>
            </div>

            {e.description && (
              <section className={css.sec}>
                {options.aboutHeading && (
                  <h2 className={css.h2} {...part('about-heading')}>
                    {options.aboutHeading}
                  </h2>
                )}
                <div className={css.body} {...part('description')}>
                  <Paragraphs text={e.description} />
                </div>
              </section>
            )}

            <dl className={css.dl} {...part('details')}>
              <div {...part('location')}>
                <dt>Location</dt>
                <dd>
                  {where.t1}
                  {where.t2 && <small>{where.t2}</small>}
                </dd>
              </div>
              {e.cancellationText.trim() && (
                <div {...part('cancellation')}>
                  <dt>Cancellation</dt>
                  <dd>{e.cancellationText}</dd>
                </div>
              )}
            </dl>
            {message && <div className={css.message}>{message}</div>}
          </article>
          <div className={css.side} {...part('booking')}>
            {booking}
          </div>
        </div>
      </div>
    </div>
  );
}
